import moment from 'moment';
import channelsUtil from '../../../helpers/channelsUtil';
import h from '../../../helpers/common';
import f from '../../../helpers/formatter';
import { __ } from '../../../translations';
import { getCountryPPVEvents } from '../../../utils/user';
import { getReminderText, getTimer } from '../components/elements/componentsUtil';
import { PROMOTIONS_QUERY_KEY } from '../constants';
import { getPromotionCTAUrl } from '@intigral/utils/lib/helpers/promotion';
import url from '../../../helpers/url';
import user from '../../../helpers/user';
import { banner_resolution } from '../constants';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import f2 from '../../../helpers/formatter2';
import { checkShowLockIcon } from '../../../helpers/vod';

export const createPromotionObject = (promo, promoHelpers, language) => {
    const isRTL = language === 'ar';
    const { getPromoButtons, getPromoImages, getProviderLogo } = promoHelpers;
    const subGuid = promo?.subscriptionGuids;
    const isLocked = checkShowLockIcon(subGuid ?? []);

    // ---- Base object ----
    const promotion: any = {
        buttons: getPromoButtons(promo).buttonsArray,
        images: getPromoImages(promo),
        providerLogo: getProviderLogo(promo),
        duration: formatTime(promo.duration, promo.promoType),
        genre: Array.isArray(promo.genre) ? promo.genre.map((g: string) => __(g)) : [],
        title: ['CHANNEL', 'LIVESTREAM'].includes(promo.promoType) ? promo.title : '',
        logo: promo.logo,
        programDescription: !isRTL ? promo.programDescription?.en : promo.programDescription?.ar,
        reminderText: !isRTL ? promo.cmsDesc?.en : promo.cmsDesc?.ar,
        year: promo.year,
        promotionRaw: promo,
        programTitle: promo?.ppvEvent?.name,
        isLockedLinear: ['CHANNEL', 'LIVESTREAM'].includes(promo.promoType) ? isLocked : false,
    };

    // ---- Localized program title for rewind/catchup ----
    if ((promo.programTitle && promo.promoType === 'REWIND') || promo.promoType === 'CATCHUP') {
        promotion.programTitle = isRTL
            ? promo.programTitle.ar ?? promo._text.ar ?? ''
            : promo.programTitle.en ?? promo._text.en ?? '';
    }

    // ---- Start time for rewind/catchup ----
    if ((promo.startTime && promo.promoType === 'REWIND') || promo.promoType === 'CATCHUP') {
        promotion.startTime = `${f.formatRewindDate(promo.startTime, true)} - ${f.formatRewindDate(
            promo.endTime,
            false
        )}`;
    }

    // ---- Season & Episode info ----
    if (promo.seasonNumber) {
        const space = isRTL ? ' ' : '';
        if (promo.promoType === 'SEASON') {
            promotion.season = `${__('promo_season_text')} ${promo.seasonNumber}`;
            promotion.numberOfEpisodes = `${__('promo_episode_text')} 1-${promo.noOfEpisodes}`;
        } else if (promo.promoType === 'EPISODE') {
            promotion.season = `${__('promo_season_code')}${space}${promo.seasonNumber}`;
            promotion.episodeNumber = `${__('promo_episode_code')}${space}${promo.episodeNumber}`;
        }
    }

    // ---- Channel info ----
    if (promo.channelNumber && ['REWIND', 'CHANNEL', 'CATCHUP', 'LIVESTREAM'].includes(promo.promoType)) {
        promotion.channelNumber = `${__('promo_channel_code')} ${promo.channelNumber}`;
    }

    // ---- Rating ----
    if (promo.ratings && !['BANNER', 'SUBSCRIPTION', 'CHANNEL'].includes(promo.promoType)) {
        promotion.rating = __(`rating_${promo?.ratings?.toLowerCase()}`);
    }

    // ---- Livestream ----
    if (promo.promoType === 'LIVESTREAM') {
        const { getFormattedTime } = channelsUtil;
        const program = promo.liveStream;

        promotion.programTitle = isRTL ? program?.localizedTitle?.ar || '' : program?.title || '';
        promotion.programDescription = isRTL ? program?.localizedDescription?.ar || '' : program?.description || '';
        promotion.startTime = `${getFormattedTime(program?.startTime, 'hh:mm a')} - ${getFormattedTime(
            program?.endTime,
            'hh:mm a'
        )}`;
        promotion.live = __('streaming_carousel_live_badge_text');
    }

    // ---- PPV ----
    if (promo.promoType === 'PPV') {
        const {
            eventStartDate,
            eventStartTime,
            eventBroadcastTs,
            isLive,
            isCompleted,
            program = null,
            rewindDays,
        } = promo?.ppvEvent ?? {};

        promotion.startTime = eventStartDate;
        promotion.year = eventStartTime;
        promotion.programDescription = !isRTL ? program?.description : program?.descriptionLocalized?.ar;

        promotion.reminderText = getReminderText(rewindDays);
        if (!isLive && !isCompleted) promotion.extraContent = getTimer(eventBroadcastTs);

        promotion.rating = __(`rating_${h.getGenreRating(program?.ratings)}`) ?? null;
        if (isLive) promotion.live = __('streaming_carousel_live_badge_text');
    }

    return promotion;
};

export async function processPromotions(promotions, channels, ppvEventConfig, cmsPPVEvents, operators, countryCode) {
    const ppvEvents = getCountryPPVEvents(ppvEventConfig, operators, countryCode);

    const results = await Promise.all(
        (promotions ?? []).map(async (promo) => {
            try {
                if (promo.promoType === 'CATCHUP') {
                    const channel = channelsUtil.getChannelByChannelId(promo.channelId);

                    if (!channel) return promo;

                    return {
                        ...promo,
                        channelGuid: channel.guid,
                        channel,
                    };
                }

                if (promo.promoType === 'PPV') {
                    const filteredChannels = channelsUtil.getFilterChannelBySubGuid(channels, promo.subscriptionGuids);

                    const channel =
                        Array.isArray(filteredChannels) && filteredChannels.length > 0 ? filteredChannels[0] : null;

                    const ppvEvent = ppvEvents?.find((event) =>
                        promo.subscriptionGuids.includes(event?.subscriptionGuid)
                    );
                    const cmsPPVEvent = cmsPPVEvents?.find((event) =>
                        promo.subscriptionGuids.includes(event?.subscriptionGuid)
                    );

                    if (ppvEvent && ppvEvent.isActive && channel && cmsPPVEvent) {
                        const { getFormattedTime, isNow, isEarlier } = channelsUtil;
                        const { eventBroadcastTs, programGuid, eventExpiryDate } = ppvEvent;
                        const { eventEndTimeTs } = cmsPPVEvent;

                        const response = await getPPVProgram(programGuid);

                        const timeObj = { startTime: eventBroadcastTs, endTime: eventEndTimeTs };
                        const isLive = isNow(timeObj);
                        const isCompleted = isEarlier(timeObj);
                        const rewindDays = isCompleted
                            ? Math.ceil(moment(eventExpiryDate).diff(moment(), 'days', true))
                            : null;

                        const eventStartDate = getFormattedTime(eventBroadcastTs, 'MMMM D, YYYY');
                        const eventStartTime = getFormattedTime(eventBroadcastTs, 'h:mm A');

                        return {
                            ...promo,
                            channelId: channel?.channelID,
                            ppvEvent: {
                                ...ppvEvent,
                                eventStartDate,
                                eventStartTime,
                                eventEndTimeTs,
                                isLive,
                                isCompleted,
                                program: response,
                                channel,
                                rewindDays,
                            },
                        };
                    }

                    return null;
                }

                return promo;
            } catch (innerError) {
                return null;
            }
        })
    );

    return results.filter(Boolean);
}

/**
 * Function to save promotions to shared React Query cache
 * Call this from components that fetch promotions (like FeaturedSlider)
 */
export const savePromotionsToCache = (queryClient: any, promotions: any[]) => {
    queryClient.setQueryData(PROMOTIONS_QUERY_KEY, promotions);
};

/**
 * Function to get promotions from shared React Query cache
 * Use this in components that need promotions but don't fetch them
 */
export const getPromotionsFromCache = (queryClient: any): any[] => {
    const promotions = queryClient.getQueryData(PROMOTIONS_QUERY_KEY);
    return promotions || [];
};

/**
 * Retrieves the appropriate mobile image URL for a given item.
 *
 * @param {Object} item - The content item containing image data.
 * @returns {string|null} - The selected image URL or null if not found.
 */
export const getMobileImages = (item) => {
    if (!item) return null;

    const mobileImages = item.images?.filter((img) => img.id === '396x586');
    const imageUrl = mobileImages?.[1]?.url || mobileImages?.[0]?.url || item.image || null;
    return imageUrl;
};

/**
 * Formats a duration (in seconds) into a human-readable string in minutes.
 * Only applies to valid promo types: 'VOD' and 'EPISODE'.
 *
 * @param {number} durationInSeconds - The duration in seconds.
 * @param {string} promoType - The type of promo (e.g., 'VOD', 'EPISODE', 'LIVE', etc.).
 * @returns {string|null} - A formatted duration string (e.g., "45 min") or null if invalid.
 */
export const formatTime = (durationInSeconds: number, promoType: string) => {
    const validTypes = ['VOD', 'EPISODE'];

    if (!validTypes.includes(promoType)) return null;
    if (typeof durationInSeconds !== 'number' || durationInSeconds <= 0) return null;

    const minutes = f2.convertSecondsToMinutes(durationInSeconds);
    return `${minutes} ${__('min')}`;
};

export async function getPPVProgram(byGuid: string): Promise<any[]> {
    try {
        const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
        const result = await mpxService.getFeed('search-all-pa', { byGuid });
        return result?.entries?.[0] ?? null;
    } catch (e) {
        return [];
    }
}

/** ********************** Horizontal Banner ********************** **/

export const getHBPromotions = (content = []) => {
    if (!Array.isArray(content)) return [];
    const { mobile, desktop } = banner_resolution;

    return content.reduce((acc, promotion) => {
        const desktopImg = getHorizontalBannerImage(promotion, desktop);
        const mobileImg = getHorizontalBannerImage(promotion, mobile);

        if (!desktopImg || !mobileImg) return acc;

        acc.push({
            mobileImg,
            desktopImg,
            href: getPromotionCTAUrl(promotion, url, user),
            rewindItem: promotion.promoType === 'REWIND' ? { ...promotion } : undefined,
            item: promotion,
        });

        return acc;
    }, []);
};

export const groupHBPromotions = (promotions) => {
    return promotions.reduce((acc, item) => {
        const type = item.item?.promoType || 'default';
        if (!acc[type]) acc[type] = [];
        acc[type].push(item);
        return acc;
    }, {});
};

export const normalizeHBUIData = (grouped, onClickHandler) => {
    const normalized = {};

    Object.entries(grouped).forEach(([type, items]) => {
        normalized[type] = Array.isArray(items)
            ? items.map((item) => ({
                  mainImage: { src: item.desktopImg },
                  srcMobile: item.mobileImg,
                  href: item.href,
                  rewindItem: item.rewindItem,
                  onClick: onClickHandler,
                  item: item.item,
              }))
            : [];
    });

    return normalized;
};

/**
 * Get image URL based on resolution and device type
 * @param {Object} item - Promotion item object containing images
 * @param {String} folder - Folder of the image (e.g., 'horizontal_phone', 'horizontal_web')
 * @returns {String | null} URL of the image if found, otherwise null
 */
export const getHorizontalBannerImage = (item, folder) => {
    if (!item?.images?.length || !folder) return null;

    const match = item.images.find((img) => img.url?.includes(`/${folder}/`));
    return match ? h.getImagizerUrl(match.url, 0, 0, 90) : null;
};

export const getLocalizedTitle = (obj, lang) => {
    if (!obj) return '';
    return lang === 'ar' ? obj?.ar || '' : obj?.en || '';
};
